import type { ResumeDto } from "@izicvs/dto";
import { useMutation, useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export type Organization = {
  id: string;
  name: string;
  plan: string;
  billingModel: string;
  seatLimit: number | null;
  logo: string | null;
  role: string;
  seats: number;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  tokenBalance: number;
};

export type OrgMember = {
  id: string;
  role: string;
  createdAt: string;
  user: { id: string; name: string; email: string; picture: string | null };
};

const invalidate = () => queryClient.invalidateQueries({ queryKey: ["organizations"] });

export const fetchMyOrganizations = async () => {
  const response = await axios.get<Organization[]>("/organizations/mine");
  return response.data;
};

export const useMyOrganizations = () => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations"],
    queryFn: fetchMyOrganizations,
  });
  return { organizations: data, loading: isPending };
};

export const createOrganization = async (payload: { name: string; plan?: string }) => {
  const response = await axios.post<Organization>("/organizations", payload);
  return response.data;
};

export const useCreateOrganization = () => {
  const { mutateAsync: create, isPending: loading } = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => void invalidate(),
  });
  return { create, loading };
};

export const useUpdateOrganization = () => {
  const { mutateAsync: update, isPending: loading } = useMutation({
    mutationFn: ({
      organizationId,
      ...payload
    }: {
      organizationId: string;
      name?: string;
      logo?: string | null;
      plan?: string;
    }) => axios.patch<Organization>(`/organizations/${organizationId}`, payload),
    onSuccess: () => void invalidate(),
  });
  return { update, loading };
};

export const fetchOrgMembers = async (organizationId: string) => {
  const response = await axios.get<OrgMember[]>(`/organizations/${organizationId}/members`);
  return response.data;
};

export const useOrgMembers = (organizationId: string | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "members"],
    queryFn: () => fetchOrgMembers(organizationId!),
    enabled: Boolean(organizationId),
  });
  return { members: data, loading: isPending };
};

export type CohortMember = {
  user: { id: string; name: string; email: string; picture: string | null };
  role: string;
  resumes: number;
  sent: number;
  callbacks: number;
  offers: number;
  callbackRate: number;
  tokensUsed: number;
};

export type Cohort = {
  totals: {
    members: number;
    resumes: number;
    sent: number;
    callbacks: number;
    offers: number;
    callbackRate: number;
    tokensUsed: number;
  };
  members: CohortMember[];
};

export const useCohort = (organizationId: string | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "cohort"],
    queryFn: async () => {
      const response = await axios.get<Cohort>(`/organizations/${organizationId!}/cohort`);
      return response.data;
    },
    enabled: Boolean(organizationId),
  });
  return { cohort: data, loading: isPending };
};

export type OrgPool = {
  allowance: number;
  used: number;
  balance: number;
  remaining: number;
  billingEnabled: boolean;
};

export const useOrgPool = (organizationId: string | undefined) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "pool"],
    queryFn: async () => {
      const response = await axios.get<OrgPool>(`/organizations/${organizationId!}/pool`);
      return response.data;
    },
    enabled: Boolean(organizationId),
  });
  return { pool: data, loading: isPending };
};

export const useOrgCheckout = () => {
  const { mutateAsync: checkout, isPending: loading } = useMutation({
    mutationFn: async ({
      organizationId,
      plan,
      seats,
    }: {
      organizationId: string;
      plan: "pro" | "autopilot";
      seats?: number;
    }) => {
      const response = await axios.post<{ url: string }>(
        `/organizations/${organizationId}/checkout`,
        { plan, seats },
      );
      return response.data;
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  return { checkout, loading };
};

export const useOrgBillingPortal = () => {
  const { mutateAsync: openPortal, isPending: loading } = useMutation({
    mutationFn: async (organizationId: string) => {
      const response = await axios.post<{ url: string }>(
        `/organizations/${organizationId}/billing-portal`,
      );
      return response.data;
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  return { openPortal, loading };
};

export const useBuyTokens = () => {
  const { mutateAsync: buyTokens, isPending: loading } = useMutation({
    mutationFn: async ({
      organizationId,
      quantity,
    }: {
      organizationId: string;
      quantity: number;
    }) => {
      const response = await axios.post<{ url: string }>(
        `/organizations/${organizationId}/tokens/checkout`,
        { quantity },
      );
      return response.data;
    },
  });
  return { buyTokens, loading };
};

export const useMemberResumes = (
  organizationId: string,
  memberUserId: string,
  enabled: boolean,
) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "member", memberUserId, "resumes"],
    queryFn: async () => {
      const response = await axios.get<ResumeDto[]>(
        `/organizations/${organizationId}/members/${memberUserId}/resumes`,
      );
      return response.data;
    },
    enabled,
  });
  return { resumes: data, loading: isPending };
};

export type MemberApplication = {
  id: string;
  jobTitle: string;
  company: string;
  status: string;
  url: string | null;
  updatedAt: string;
};

export const useMemberApplications = (
  organizationId: string,
  memberUserId: string,
  enabled: boolean,
) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "member", memberUserId, "applications"],
    queryFn: async () => {
      const response = await axios.get<MemberApplication[]>(
        `/organizations/${organizationId}/members/${memberUserId}/applications`,
      );
      return response.data;
    },
    enabled,
  });
  return { applications: data, loading: isPending };
};

export type MemberAnalysis = {
  id: string;
  jobTitle: string;
  resumeTitle: string;
  atsScore: number;
  shortlist: "yes" | "maybe" | "no";
  createdAt: string;
};

export const useMemberAnalyses = (
  organizationId: string,
  memberUserId: string,
  enabled: boolean,
) => {
  const { data, isPending } = useQuery({
    queryKey: ["organizations", organizationId, "member", memberUserId, "analyses"],
    queryFn: async () => {
      const response = await axios.get<MemberAnalysis[]>(
        `/organizations/${organizationId}/members/${memberUserId}/analyses`,
      );
      return response.data;
    },
    enabled,
  });
  return { analyses: data, loading: isPending };
};

export const useAddMember = () => {
  const { mutateAsync: addMember, isPending: loading } = useMutation({
    mutationFn: ({ organizationId, email }: { organizationId: string; email: string }) =>
      axios.post(`/organizations/${organizationId}/members`, { email }),
    onSuccess: () => void invalidate(),
  });
  return { addMember, loading };
};

export const useRemoveMember = () => {
  const { mutateAsync: removeMember } = useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
      axios.delete(`/organizations/${organizationId}/members/${userId}`),
    onSuccess: () => void invalidate(),
  });
  return { removeMember };
};

// Emails the member that an admin has started managing their account.
export const useNotifyMemberManaged = () => {
  const { mutateAsync: notifyManaged } = useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
      axios.post(`/organizations/${organizationId}/members/${userId}/notify-managed`),
  });
  return { notifyManaged };
};
